import { useEffect, useState } from "react";
import { Button } from "../components/ui";
import { Call, multiple } from "../components/game";
import { big, fmt, fromRaw, MINUS, paper, ratioBp, usd } from "../lib/num";
import { openPosition } from "../state/actions";
import { marks } from "../state/market";
import { dismissMoment, moment, type Moment as Shown } from "../state/moments";
import { openPaper } from "../state/sheets";
import { resultOf, type Play } from "../state/record";
import { useStore } from "../state/store";
import "./moment.css";

const CENTS = 10n ** 16n;
/** A win worth a shine: double the stake or more, or $250 and up. */
const BIG_BP = 20_000n;
const BIG_USD = 250n * 10n ** 18n;

/** `|raw|` dollars counting up from zero once, then held: for the screen only. */
function CountUp({ raw, sign }: { raw: bigint; sign: string }) {
  const cents = (raw < 0n ? -raw : raw) / CENTS;
  const [shown, setShown] = useState(0n);
  useEffect(() => {
    const total = Number(cents);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 700);
      const eased = 1 - (1 - t) ** 3;
      setShown(t >= 1 ? cents : BigInt(Math.round(total * eased)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [cents]);
  return (
    <>
      {sign}
      {fmt(fromRaw(shown, 2), { prefix: "$", places: 2 })}
    </>
  );
}

function words(plays: Play[]) {
  const first = plays[0] as Play;
  const net = plays.reduce((sum, play) => sum + big(play.net), 0n);
  const staked = plays.reduce((sum, play) => sum + big(play.margin), 0n);
  const back = staked + net > 0n ? staked + net : 0n;
  const minted = plays.reduce((sum, play) => sum + big(play.paper), 0n);
  const busts = plays.filter((play) => play.busted).length;
  const tone = net > 0n ? "win" : busts === plays.length ? "bust" : "loss";
  let title: string;
  if (plays.length > 1) title = net > 0n ? `Cashed out ${plays.length} calls` : `${plays.length} calls closed`;
  else title = resultOf(first) === "win" ? "Cashed out" : first.busted ? "Busted" : "Closed";
  let sub: string;
  if (tone === "bust") sub = plays.length > 1 ? `${usd(staked)} staked, all busted.` : `Your ${usd(staked)} stake is gone.`;
  else sub = `${multiple(ratioBp(back, staked))} your ${usd(staked)} stake`;
  const shine = tone === "win" && (ratioBp(back, staked) >= BIG_BP || net >= BIG_USD);
  return { net, staked, minted, tone, title, sub, shine };
}

function Card({ shown }: { shown: Shown }) {
  const { plays, streak, key } = shown;
  const { net, minted, tone, title, sub, shine } = words(plays);
  const only = plays.length === 1 ? plays[0] : undefined;
  const [held, setHeld] = useState(false);
  const [busy, setBusy] = useState(false);
  const life = tone === "win" ? 7_000 : 6_000;

  const again = async () => {
    if (!only || busy) return;
    setBusy(true);
    try {
      await openPosition(
        {
          instrumentId: only.instrumentId,
          isLong: only.isLong,
          margin: fromRaw(only.margin, 18),
          leverage: only.leverage,
          useDebt: false,
          mark: marks.get()[only.instrumentId]?.raw ?? null,
        },
        `Call ${only.isLong ? "▲ Up" : "▼ Down"} ${only.symbol} ${only.leverage}× · ${usd(only.margin)}`,
      );
      dismissMoment(key);
    } catch {
      // told by a toast
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`mo mo-${tone} ${shine ? "is-big" : ""} ${held ? "is-held" : ""}`}
      role="status"
      aria-live="polite"
      onPointerEnter={() => setHeld(true)}
      onPointerLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
    >
      <span className="mo-glow" aria-hidden />
      <div className="mo-head">
        <span className="mo-title">{title}</span>
        {only && (
          <span className="mo-call">
            <span className="mo-symbol">{only.symbol}</span>
            <Call isLong={only.isLong} leverage={only.leverage} />
          </span>
        )}
      </div>
      <div className="mo-figure num">
        <CountUp raw={net} sign={net > 0n ? "+" : net < 0n ? MINUS : ""} />
      </div>
      <div className="mo-sub">{sub}</div>
      {(streak >= 2 || minted > 0n) && (
        <div className="mo-meta">
          {streak >= 2 && <span className="mo-streak">{streak} in a row</span>}
          {minted > 0n && (
            <span>
              +{paper(minted)} PAPER ·{" "}
              <button
                type="button"
                className="link-btn mo-link"
                onClick={() => {
                  dismissMoment(key);
                  openPaper();
                }}
              >
                Stake it ›
              </button>
            </span>
          )}
        </div>
      )}
      <div className="mo-actions">
        {only && (
          <Button tone="primary" size="md" busy={busy} onClick={() => void again()}>
            Run it back
          </Button>
        )}
        <Button tone="ghost" size="md" onClick={() => dismissMoment(key)}>
          {only ? "Done" : "Close"}
        </Button>
      </div>
      <span className="mo-timer" aria-hidden style={{ ["--life" as string]: `${life}ms` }} onAnimationEnd={() => dismissMoment(key)} />
    </div>
  );
}

/** How the last call ended, once, over whatever page is open. */
export function MomentHost() {
  const shown = useStore(moment);
  if (!shown) return null;
  return (
    <div className="mo-host">
      <Card key={shown.key} shown={shown} />
    </div>
  );
}
